/**
 * Wire protocol and tuning constants shared by the server and the browser.
 *
 * The frontend imports this file directly (Vite alias `@shared` →
 * `backend/src/shared`). Nothing in here may import from `@nestjs/*`, Prisma,
 * or anything else Node-only — it has to be loadable in a browser bundle.
 */
import type { TankSpec } from './tank-spec';
import type { ActiveBuff, AirdropKind } from './airdrops';
// The only value import in this file, and it is one string. `maps.ts` imports
// `TeamId` from here as a *type*, which is erased, so there is no runtime cycle.
// The default team size joins it, for the reason written on `DEFAULT_TEAM_SIZE`:
// team size is a property of a map now, so the default belongs beside the
// property, and this file re-exports it under the name every caller already uses.
import { DEFAULT_MAP_ID, DEFAULT_TEAM_SIZE, teamSizeFor } from './maps';
// Type-only, and erased — `maps.ts` still has no runtime edge back to this file.
import type { MapDefinition } from './maps';

// ─── Simulation rates ────────────────────────────────────────────────────────

/** Server simulation ticks per second. */
export const TICK_RATE = 30;
/** Seconds per tick. Every state advance uses exactly this — never a wall-clock delta. */
export const TICK_DT = 1 / TICK_RATE;
/** How often the client samples input and sends it to the server. */
export const INPUT_RATE = 30;
/**
 * How often the server broadcasts a snapshot of the arena. Kept equal to the
 * tick rate: at this player count the bandwidth is irrelevant, and sending
 * every tick gives the client a deep enough buffer to interpolate smoothly.
 */
export const SNAPSHOT_RATE = TICK_RATE;

/**
 * How far in the past remote tanks are rendered, in milliseconds. Buys us two
 * snapshots of buffer so interpolation always has a pair to work between —
 * without it, remote tanks stutter whenever a packet is late.
 */
export const INTERPOLATION_DELAY_MS = 100;

// ─── Tank tuning ─────────────────────────────────────────────────────────────

export const MAX_FORWARD_SPEED = 14; // metres/second
export const MAX_REVERSE_SPEED = 6;
export const ACCELERATION = 18; // m/s²
/**
 * Seconds a tank takes to brake from its own top speed to a dead stop, under
 * **active** braking — the player pressing the opposite key to current motion,
 * not merely letting go. The rate this implies is derived per hull from its own
 * `maxForwardSpeed` in `stepTank`, rather than being one flat deceleration:
 * a flat rate would stop the roster's fastest hull far short of its slowest
 * one's time, and the brief is that every hull brakes in roughly the same
 * window regardless of how fast it was going.
 */
export const BRAKE_STOP_SECONDS = 1.2;
/**
 * Seconds a tank takes to **coast** to a stop from its own top speed —
 * releasing the throttle and letting rolling resistance bring it down, with
 * nothing held. Derived per hull exactly like `BRAKE_STOP_SECONDS`, and
 * deliberately longer: distinctly slower than the ~1.2 s of an actual brake.
 */
export const COAST_STOP_SECONDS = 3;
/** Hull yaw rate. Deliberately slow — the turret is what aims. */
export const HULL_TURN_RATE = 1.7; // radians/second
/**
 * Turret slew rate.
 *
 * The turret is a rate-controlled axis, not a pointing device: the client sends
 * a direction to rotate, never a target angle. That means a modified client
 * cannot snap-aim by construction — there is no angle in the protocol for it to
 * lie about.
 */
export const TURRET_SLEW_RATE = 3.2; // radians/second

/**
 * How far the playable rectangle reaches from the origin, per world axis.
 *
 * **A pair, not a scalar, and the axes are `x` and `z` rather than a width and a
 * length.** The simulation clamps in world axes, so a name that has to be
 * resolved against a map's orientation is a name that will be resolved wrongly
 * once. There is deliberately no third form: an optional `z` defaulting to `x`
 * would compile at every one of the eighty-odd call sites while half of them
 * silently read the x-extent for both axes, which is precisely the failure this
 * shape exists to make impossible.
 *
 * A tank's bound on either axis is that axis's half-extent less `TANK_RADIUS`.
 * Nothing derives one axis from the other.
 */
export interface ArenaHalfExtent {
  readonly x: number;
  readonly z: number;
}

/**
 * Half-extent of the default arena's square, in metres.
 *
 * Kept as a scalar because the default arena genuinely is square, and because
 * the tests and probes that legitimately have no map to ask want one number.
 * `ARENA_HALF_EXTENT` below is the pair every simulation entry point actually
 * takes; this is where its two components come from, stated once so "the default
 * arena is square" is a fact of the source rather than a coincidence of two
 * literals.
 */
export const ARENA_HALF_SIZE = 100;

/**
 * The arena's half-extent — **the default a map takes when it does not state its
 * own.**
 *
 * It stopped being a global the day one battlefield wanted to be a different
 * size from the rest. A map declares `halfExtent` and the arena resolves it once
 * in its constructor, exactly as it resolves `mapId` and `bodies`; everything in
 * the simulation that used to read a module constant now takes the pair as an
 * argument and falls back to this. That is not a second global — it is the
 * default for the callers that legitimately have no map to ask: unit tests, the
 * divergence probe, and `stepTank` invoked with four arguments the way it was
 * before scenery existed.
 *
 * **The ordering rule is the whole risk here.** `distanceToWall` and the
 * position clamp inside `stepTank` both read it, so the client predicts against
 * it — which means it has to reach `PredictedTank.setHalfSize` *before the first
 * reconcile*, on the same footing as `setBodies` and `setSpec`. A client holding
 * the wrong arena size disagrees with the server on every tick spent near a
 * wall: a small permanent error, under `SNAP_THRESHOLD`, never counted as a
 * snap, dragged out by the error easing forever. See the smoothness invariants
 * in `CLAUDE.md` — that is the exact shape of three bugs already documented
 * there.
 *
 * **A per-axis bound has the same failure mode one axis deeper.** A client that
 * clamped both axes against the x-extent of a rectangle would agree with the
 * server everywhere except along the two long walls — which is the same
 * permanent sub-snap error, and rarer, and therefore harder to see. Both sides
 * run this file; neither may hold a scalar.
 */
export const ARENA_HALF_EXTENT: ArenaHalfExtent = {
  x: ARENA_HALF_SIZE,
  z: ARENA_HALF_SIZE,
};

/** Keeps the hull from clipping through the arena wall. */
export const TANK_RADIUS = 3;

// ─── Combat tuning ───────────────────────────────────────────────────────────

/**
 * Radius of the cylinder a shell has to touch to count as a hit.
 *
 * Slightly under `TANK_RADIUS`: the collision radius keeps a 3.2 × 5.4 m hull
 * clear of the wall, but a shot that only grazes that circle visibly misses.
 */
export const TANK_HIT_RADIUS = 2.6;

/**
 * Distance from the tank's centre to the muzzle, along the turret's facing.
 *
 * Measured from the authored turret: its barrel axis spans 0.979 model units
 * and is scaled by ~4.09, putting the tip 2.0 m from the turret centre. The
 * previous 5.3 m was carried over from the placeholder's much longer barrel and
 * spawned every shot three metres out in open air.
 *
 * `TankVisual.muzzleWorld()` imports this rather than repeating it, so the
 * tracer can never leave from somewhere other than where the server fired it.
 */
export const MUZZLE_OFFSET = 2.05;
/** Height of the muzzle above the ground, in metres. Purely cosmetic. */
export const MUZZLE_HEIGHT = 1.7;

/** Seconds a destroyed tank stays out of the arena before respawning. */
export const RESPAWN_DELAY_SECONDS = 3;
/** Ticks a destroyed tank stays out. Derived so it's exact in tick terms. */
export const RESPAWN_DELAY_TICKS = Math.round(RESPAWN_DELAY_SECONDS * TICK_RATE);

/**
 * Seconds a destroyed human sits dead in a team battle before inheriting a
 * teammate's tank.
 *
 * A team-battle death used to resolve `takeOver` synchronously, on the same
 * tick as the kill — no window in which the player was a spectator. That
 * meant no death screen could ever appear for it either, since `alive` never
 * actually went false on any snapshot the client received. This is the one
 * number that reopens that window: `destroy()` now leaves the victim dead for
 * this long before `takeOver` actually runs, so a real snapshot shows them
 * destroyed and the client can draw the same wreck-and-killer screen a
 * deathmatch respawn already draws.
 *
 * Set equal to `RESPAWN_DELAY_SECONDS` on purpose — a team battle's pause
 * should feel like the deathmatch one, not like a different game — but kept
 * as its own constant because the two are different tunables: one paces "how
 * long until my own tank is back", the other paces "how long until I'm back
 * in a fight at all", and a designer retuning one must not silently retune
 * the other.
 */
export const TEAM_BATTLE_TAKEOVER_DELAY_SECONDS = 3;
/** Ticks version of the above. Derived so both sides agree exactly. */
export const TEAM_BATTLE_TAKEOVER_DELAY_TICKS = Math.round(
  TEAM_BATTLE_TAKEOVER_DELAY_SECONDS * TICK_RATE,
);

/**
 * Shells a single player may have in flight at once.
 *
 * The reload already bounds the fire rate; this bounds the damage a very long
 * range spec could have travelling at any one moment, and stops a pathological
 * spec from filling the arena with entities.
 *
 * Raised from 8 when multi-shell weapons arrived: a scattergun puts eight
 * pellets up in a single trigger pull and would otherwise have been permanently
 * one volley from its own ceiling. The cap is checked per *volley* — a volley
 * that does not fit whole is refused whole, so a shotgun never fires a partial
 * spread that would silently miss.
 */
export const MAX_SHELLS_PER_PLAYER = 24;

/**
 * How much of a blast's damage survives to its outer edge, as a fraction.
 *
 * A mortar that did full damage across its whole radius would make aim
 * irrelevant; one that fell off to nothing would make the radius decorative.
 */
export const BLAST_EDGE_FRACTION = 0.35;

/**
 * Health lost per second for loitering inside an enemy's spawn zone, as a
 * **fraction of the tank's own maximum**.
 *
 * A fraction rather than a flat figure so every tank in the game dies to it in
 * the same time — 12.5 seconds from full, whether it is a Recruit hull or a
 * Bastion. A flat rate made this a tax on light tanks and a rounding error on
 * heavy ones, which is backwards: the thing being discouraged is *holding* an
 * enemy spawn, and the builds most able to hold one were the ones it barely
 * touched.
 *
 * **Bypasses armour on purpose.** `applyDamage`'s `(damage, armour)` shape is
 * not used for this: armour already blunts every gun in the game by up to
 * 80%, and letting it blunt this too would restore exactly the asymmetry the
 * fraction removes. It is not gunfire — it is the base's own position refusing
 * to be held — so it is applied as flat health loss.
 *
 * Only ever measured against the **enemy's** base. `Arena.applySpawnZoneDamage`
 * never even asks the question for a player's own side's base, so "does this
 * damage my own team" has no branch to get backwards — it is a fact about
 * which base is being checked, not a condition on top of a shared one.
 */
export const SPAWN_ZONE_DAMAGE_FRACTION_PER_SECOND = 0.08;

/**
 * How long a tank may stand in an enemy spawn before the damage starts.
 *
 * The grace is what makes "passing through is not camping" a *rule* rather
 * than an intention. It used to be arithmetic: damage began on contact and a
 * drive-through simply cost less than a stay. That reads as a bug from inside
 * the tank — health falling with nothing shooting at you and no warning that
 * had time to land — and it is what "too quick to react" was reporting.
 *
 * Now nothing at all happens for six seconds, the HUD says so from the first
 * frame inside, and the countdown on screen is the countdown the server is
 * keeping.
 *
 * **Spent and recovered at the same rate.** `ArenaPlayer.spawnZoneTicks`
 * counts up while inside and down while outside, clamped to this window at
 * both ends, so leaving and immediately re-entering does not buy a fresh six
 * seconds — earning the grace back costs exactly as long as spending it did.
 * A counter that reset on exit would make the whole mechanic optional to
 * anybody willing to tap reverse.
 */
export const SPAWN_ZONE_GRACE_SECONDS = 6;

/** Ticks version of the above. Derived so both sides agree exactly. */
export const SPAWN_ZONE_GRACE_TICKS = Math.round(SPAWN_ZONE_GRACE_SECONDS * TICK_RATE);

// ─── Teams ───────────────────────────────────────────────────────────────────

/**
 * Which side a tank fights on.
 *
 * Deliberately neutral colours rather than `ally` / `enemy`. Those two words are
 * *relative to whoever is looking*: a player balanced onto the second team would
 * be told by the wire that they are their own enemy. The client compares a
 * tank's team against its own and paints friend or foe from that — which is the
 * only place the question has an answer.
 */
export type TeamId = 'red' | 'blue';

/** Both sides, in a fixed order. Team assignment iterates this. */
export const TEAMS: readonly TeamId[] = ['red', 'blue'];

/**
 * Tanks per side, humans and bots together — **the default a map takes when it
 * does not state its own.**
 *
 * Bots top each side up to it and stand down again as humans arrive, so the
 * arena is always the same size and a lone player always has a full fight.
 *
 * Like `ARENA_HALF_EXTENT` this is now a property of the map, resolved once by the
 * arena's constructor. A map with room for fifteen a side says so
 * (`MapDefinition.teamSize`); the six that do not are the standard three, and
 * the number they get is this one. Keeping it here rather than inventing a
 * second constant is what makes "unstated means the usual fight" true by
 * construction.
 */
export const TEAM_SIZE = DEFAULT_TEAM_SIZE;

/**
 * Humans one arena on this map will seat.
 *
 * Exactly the size of the match — every seat a bot could hold, a human may take
 * instead. Above it the sides would grow past the map's team size and the arena
 * would stop being the fixed-size fight the spawn slots and the bot top-up
 * assume, so a join to a full arena is refused rather than quietly widening it.
 *
 * Shared because the arena list shows it: a client that had its own idea of
 * capacity would grey out a seat the server would have given, or offer one it
 * would not. It is derived from the map rather than sent as its own number for
 * the same reason the buildings are — the client already knows which map the
 * arena is on.
 */
export function maxHumansFor(map: Pick<MapDefinition, 'teamSize'>): number {
  return teamSizeFor(map) * TEAMS.length;
}

/**
 * Humans a standard three-a-side arena seats.
 *
 * The default `maxHumansFor` produces, kept as a name because tests and the
 * lobby's copy read better for it.
 */
export const MAX_HUMANS_PER_ARENA = TEAM_SIZE * TEAMS.length;

/**
 * Whether a shell can damage a tank on the shooter's own side.
 *
 * **Off**, and deliberately so rather than by omission. Bots hold their trigger
 * whenever a shot is geometrically plausible and cannot be taught to check that
 * the whole flight path is clear of a team-mate at 90 m/s; with friendly fire on
 * they spend the match killing each other, and a mortar's eleven-metre blast
 * makes a supporting bot a liability rather than help. The same rule then has to
 * apply to humans, because a rule that binds only the AI is one players learn to
 * exploit by standing behind a bot.
 *
 * A shell still *stops* on nothing but an enemy: same-side tanks are simply not
 * in the target list a shell is swept against, so a round passes cleanly through
 * a team-mate rather than being absorbed for zero damage.
 */
export const FRIENDLY_FIRE = false;

// ─── State ───────────────────────────────────────────────────────────────────

/**
 * Everything needed to draw a tank and to continue simulating it.
 *
 * `hullYaw` and `turretYaw` are both world-space angles around +Y. The turret
 * is NOT stored relative to the hull: keeping it in world space is what lets it
 * hold its aim while the hull turns underneath it.
 */
export interface TankState {
  x: number;
  z: number;
  hullYaw: number;
  turretYaw: number;
  /** Signed speed along the hull's forward axis. Negative is reverse. */
  speed: number;
}

/**
 * One frame of player intent. Produced by the client, replayed on both sides.
 *
 * Every field is a bounded axis. There are no angles here — the camera is a
 * purely local concern and never reaches the server.
 */
export interface InputCommand {
  /** Monotonic per-player sequence number. The server echoes the last one it applied. */
  seq: number;
  /**
   * Throttle, as a fraction of the hull's top speed: -1 full reverse, 0 coast,
   * +1 full ahead. W / S send exactly -1, 0 or 1.
   *
   * Continuous for the same reason `turretTurn` below is, and the precedent is
   * deliberately that one rather than a new argument: a **thumbstick** has a
   * deflection, and rounding it to the nearest of three values threw all of it
   * away — a stick a fifth of the way forward drove at full speed, which is what
   * `TODO.md` reported. The wire carried a ternary axis for as long as the only
   * input was a keyboard, and a keyboard is what a keyboard always was.
   *
   * Note what this still is *not*: a velocity, or a position. The client says
   * "ask the engine for this fraction of its output", never "be moving at this
   * speed" — `stepTank` still integrates through `ACCELERATION` and the hull's
   * own `maxForwardSpeed`, so a hand-rolled client pinning it to 1 gains
   * precisely what holding `W` gains, which is nothing. `clampAxis` became
   * `clampUnit` at the arena's intake; the bound did not move, only the number
   * of values inside it.
   */
  forward: number;
  /**
   * Hull traverse, as a fraction of the hull's turn rate: -1 full left, +1 full
   * right, 0 straight. A / D send exactly -1, 0 or 1.
   *
   * Continuous on the same argument as `forward` — and `stepTank` already
   * multiplied by it (`input.turn * hullTurnRate * TICK_DT`), so this direction
   * of the change is the *type* catching up with arithmetic that was always
   * proportional.
   */
  turn: number;
  /**
   * Turret traverse, as a fraction of the maximum slew rate: -1 is full speed
   * counter-clockwise, +1 full speed clockwise, 0 holds.
   *
   * Continuous rather than a three-way switch so the client can request a
   * partial step and settle exactly on the angle the player is pointing at.
   * With -1|0|1 the turret could only move in whole ticks of slew and would
   * jitter by up to 6° either side of the target forever.
   *
   * Note what this still is *not*: an absolute angle. The client says "traverse
   * this fast", never "be at this bearing". A modified client that pins it to 1
   * gains nothing it could not get by holding a key, so snap-aiming remains
   * impossible by construction rather than by validation.
   */
  turretTurn: number;
  /**
   * 1 while the fire key is held, 0 otherwise. Space.
   *
   * Note what is *not* here: a direction. The shot leaves along the server's own
   * `turretYaw` for this player, so a hand-rolled client has no aim to forge —
   * the same reason the turret is a rate axis rather than a pointing device.
   */
  fire: 0 | 1;

  /**
   * A standing order for this player's own side's AI tanks. See `TeamOrder`.
   *
   * **0 or absent means "unchanged"**, which is almost every packet — an order
   * is given a handful of times a match and the input goes out thirty times a
   * second.
   *
   * This is an enum, not a target. Note especially what "hold position" is *not*
   * carrying: a point on the map. `hold` means "hold at wherever I am standing
   * right now", and the server already knows where that is, so **no coordinate
   * crosses the wire** and the rule at the top of this interface survives
   * intact. That also happens to be the better game — you drive to the ridge you
   * want held and press the key — which is the usual outcome when the honest
   * protocol and the good design agree.
   */
  order?: TeamOrderCode;
  /** How tightly the formation packs. See `FormationDensity`. 0 or absent leaves it. */
  density?: FormationDensityCode;
  /**
   * Bumped once per order the player actually issues.
   *
   * Necessary because `order` rides on a *repeating* packet. A key held down for
   * a third of a second sends the same order ten times, and re-applying `hold`
   * ten times would re-capture the anchor ten times — the held ground would
   * follow the player across the map, which is exactly the thing the order
   * exists not to do. The server acts only when this number *increases*, so a
   * repeat is idempotent and pressing the key again is deliberately not.
   *
   * It also makes the order survive the input queue's overflow trim, which drops
   * the oldest commands and could otherwise swallow the one carrying the order.
   */
  orderSeq?: number;
}

// ─── Match modes ─────────────────────────────────────────────────────────────

/**
 * What kind of battle a room is running.
 *
 * The mode is fixed for an arena's life, for the same reason its map is: a room
 * that changed the rules of death mid-match would leave every client having
 * drawn a respawn countdown for a tank that is never coming back. Picking a mode
 * chooses which room to walk into, not a setting to change inside one.
 *
 * - **`'deathmatch'`** — the original, and still the default. Everyone respawns
 *   `RESPAWN_DELAY_SECONDS` after they die, the arena tops both sides back up to
 *   `TEAM_SIZE`, and the match has no end. Nothing about it changed when team
 *   battle arrived; it *is* the old behaviour, given a name.
 *
 * - **`'team-battle'`** — nobody respawns. A destroyed tank is gone for the
 *   match. When a *human's* tank is destroyed they take over a surviving AI tank
 *   on their own side, so a player is only out of the battle once their whole
 *   side is; a side with no tanks left has lost.
 *
 * The split exists because the two want opposite things from a death. In
 * deathmatch a death costs you fifteen seconds and is the game's pacing; in a
 * team battle it is supposed to be frightening, and a respawn would remove the
 * only thing that makes holding ground matter.
 */
export type MatchMode =
  | 'deathmatch'
  | 'team-battle'
  | 'tutorial'
  | 'escort'
  | 'ctf'
  | 'wave-defence';

/**
 * The **player-versus-player** modes, in the order a picker should offer them.
 *
 * Deliberately not every `MatchMode`. The three mission modes are chosen from
 * their own section of the battle dialog, against a map they do not get to
 * pick — so listing them here would offer "Escort" beside a map chooser and
 * produce a convoy run on Ironback Ridge. `MISSION_KINDS` is their list.
 *
 * **`'team-battle'` is cut from this list, deliberately, not deleted from the
 * type.** Deathmatch is the only PvP mode offered now — the picker's Rules
 * section (a choice between one thing) was removed with it. This is the one
 * place that gates it: the DTO validates a chosen mode against this array in
 * three places, so a `team-battle` join is refused server-side even if a
 * client asks for one by hand. Everything else that already understood the
 * mode — `MATCH_MODE_NAMES`, `MATCH_MODE_BLURBS`, the arena's no-respawn path,
 * the matchmaking queue — is untouched and unused, ready if it comes back.
 */
export const MATCH_MODES: readonly MatchMode[] = ['deathmatch'];

/** The mode a deploy lands in when nobody has chosen one. */
export const DEFAULT_MATCH_MODE: MatchMode = 'deathmatch';

/** What a player sees on the mode picker. */
export const MATCH_MODE_NAMES: Readonly<Record<MatchMode, string>> = {
  deathmatch: 'Deathmatch',
  'team-battle': 'Team battle',
  tutorial: 'Basic Training',
  escort: 'Convoy Run',
  ctf: 'Relay Raid',
  'wave-defence': 'Last Stand',
};

/** One line each, for the picker. */
export const MATCH_MODE_BLURBS: Readonly<Record<MatchMode, string>> = {
  deathmatch: 'Endless. Everyone respawns.',
  'team-battle': 'No respawns. Lose your tank, take another. Last side standing wins.',
  tutorial:
    'Seven drills on the yard: look, drive, shoot, one enemy, then a section to command. ' +
    'Free to enter and it pays nothing.',
  escort: 'Walk a supply tank the length of the valley. If it burns, you failed.',
  ctf: 'Steal the signal core out of their fort and carry it home. Three times.',
  'wave-defence': 'Hold the redoubt against eight waves. No second chances mid-wave.',
};

// ─── Missions ────────────────────────────────────────────────────────────────

/**
 * The three player-versus-environment modes.
 *
 * A mission is a `MatchMode` rather than a new kind of arena, which is what
 * lets every existing thing keep working: the mode is fixed at construction
 * like the map, `quickMatchIdFor` already folds it into a room id, and
 * `outcome` / `battleOver` already mean *this is over, freeze and keep
 * broadcasting*. Nothing downstream of `enqueueInput` learns that missions
 * exist.
 */
export type MissionKind = 'tutorial' | 'escort' | 'ctf' | 'wave-defence';

/**
 * The four, in the order the missions section should offer them.
 *
 * **The tutorial is first, and it is a mission rather than a mode of its own.**
 * It was a tab in the garage and a private deathmatch on the drill yard, which
 * meant the one scripted, staged, objective-driven fight in the game was the
 * only one not written against `MissionRuntime` — so its enemies, its
 * reinforcements and its ending were all somewhere else. It is `TutorialMission`
 * now, and everything the other three get for free (asymmetric sides, no bot
 * top-up, no respawn the mission did not ask for, an objective strip, a result
 * screen, a completion record) it gets for free too.
 */
export const MISSION_KINDS: readonly MissionKind[] = [
  'tutorial',
  'escort',
  'ctf',
  'wave-defence',
];

/**
 * The missions `POST /api/battle/missions` will open a room for.
 *
 * **`MATCH_MODES`' argument, one family along, and the tutorial is the excluded
 * one for exactly the reason the mission modes are excluded from that list.** A
 * mission room is a paid sortie: it charges a fuel unit through `openSession`
 * and it banks credits and XP on the way out. The tutorial is the one join in
 * the game that does neither, and the *only* thing making that exemption safe is
 * its symmetry — a room that costs nothing must also pay nothing, or four
 * forgeable bytes buy a free farm. That symmetry is keyed on the
 * `TUTORIAL_ARENA_ID` sentinel in the gateway, which resolves to a private room
 * per player; a tutorial opened through the generic mission route would arrive
 * with a session id, be charged, and pay out.
 *
 * So the route validates against this, `ArenaService.createMission` refuses the
 * tutorial outright, and the picker still offers all four — because being
 * offered and being *creatable through that one route* are different questions,
 * and `MATCH_MODES` has answered them separately since missions landed.
 */
export const CREATABLE_MISSION_KINDS: readonly MissionKind[] = [
  'escort',
  'ctf',
  'wave-defence',
];

/**
 * The mission a mode is, or `null` if it is player-versus-player.
 *
 * The one place the two families are told apart. Written as a lookup rather
 * than `mode !== 'deathmatch' && mode !== 'team-battle'`, because that negative
 * form silently reclassifies every mode added after it — and the thing most
 * likely to be added next is another PvP mode.
 */
export function missionKindFor(mode: MatchMode): MissionKind | null {
  return mode === 'tutorial' ||
    mode === 'escort' ||
    mode === 'ctf' ||
    mode === 'wave-defence'
    ? mode
    : null;
}

/**
 * The side humans always fight on in a mission, with the enemy always `red`.
 *
 * Fixed rather than balanced, because a mission's map is asymmetric by design —
 * the convoy drives north, the flag is in the northern fort, the redoubt faces
 * the gates — so a human placed on `red` would be spawned inside the thing they
 * are meant to be attacking. `pickTeamForHuman` returns this before the
 * balancing loop rather than balancing onto one value, so the balancer is not
 * asked a question with one legal answer.
 */
export const MISSION_TEAM: TeamId = 'blue';

/**
 * The side a mission's enemies are dealt onto — the other one.
 *
 * Derived once rather than written as `MISSION_TEAM === 'blue' ? 'red' : 'blue'`
 * wherever it is needed, and that is not tidiness. Written out twice, the second
 * copy was pasted into the convoy's spawn and never edited, so **the escorted
 * vehicle was dealt onto the enemy side** — a mission whose objective was
 * hostile to the people escorting it, and which therefore halted for ever
 * because its own halt rule saw an enemy in range. One name cannot be pasted
 * wrongly.
 */
export const MISSION_FOE: TeamId = MISSION_TEAM === 'blue' ? 'red' : 'blue';

/** How hard a mission was asked for. Fixed for an arena's life, like its map. */
export type MissionDifficulty = 'standard' | 'hardened' | 'veteran';

/** The three, in ascending order. */
export const MISSION_DIFFICULTIES: readonly MissionDifficulty[] = [
  'standard',
  'hardened',
  'veteran',
];

/** What a room is created at when nobody chose. */
export const DEFAULT_MISSION_DIFFICULTY: MissionDifficulty = 'standard';

// ─── The tutorial ────────────────────────────────────────────────────────────

/**
 * The drills, in the order they are taught.
 *
 * **Strictly sequential, and one thing at a time.** The tutorial this replaced
 * opened with three goals at once — throttle, hull and turret — beside an enemy
 * tank that was already shooting, so the first thing a learner did was die while
 * reading a list. Each stage here has one idea in it, the field is empty until
 * `duel`, and nothing arrives that the previous stage has not taught the
 * controls for.
 *
 * ── It was seven, and two of them taught commanding ──────────────────────────
 *
 * `rally` asked for a `follow` order and `hold` asked for a `hold` planted on
 * marked ground, and both are **gone**: `TEAM_ORDERS_ENABLED` is off, so there
 * is no order to give and a drill that asked for one would be a drill nobody
 * could pass. They are not commented out or skipped — a skipped drill still
 * numbers the panel and still has to be reasoned about — and the count is now
 * six.
 *
 * What replaced the middle of the run is `supply`: a crate, on the far side of
 * the yard, that the player has to drive to and collect. It teaches the one
 * thing on the battlefield that is neither a control nor an enemy, it is
 * satisfied by the ordinary airdrop claim every other mode uses, and — unlike
 * the two drills it replaces — it cannot become impossible because of a switch
 * somewhere else.
 *
 * If commanding comes back, the drills come back with it as a **deliberate**
 * re-add rather than automatically: flipping `TEAM_ORDERS_ENABLED` does not
 * lengthen this list.
 */
export type TutorialStageId =
  | 'look'
  | 'drive'
  | 'fire'
  | 'duel'
  | 'supply'
  | 'assault';

/** The six, in order. `TutorialStageId` indexed, for "step 3 of 6". */
export const TUTORIAL_STAGES: readonly TutorialStageId[] = [
  'look',
  'drive',
  'fire',
  'duel',
  'supply',
  'assault',
];

/**
 * Radians of turret traverse the `look` drill wants — one full turn.
 *
 * Measured on the **turret's own world bearing**, which is what makes the drill
 * about the mouse and nothing else. The turret is not stored relative to the
 * hull, so a "turn the turret 45° relative to the hull" goal is satisfied by
 * holding `A`; this one can only be satisfied by moving the view, because the
 * gun chases the view and nothing else moves it.
 */
export const TUTORIAL_LOOK_RADIANS = Math.PI * 2;
/** Metres the `drive` drill wants on the throttle. */
export const TUTORIAL_DRIVE_METRES = 40;
/** Radians of *hull* rotation the `drive` drill wants. Half a turn. */
export const TUTORIAL_TURN_RADIANS = Math.PI;
/**
 * Volleys the `fire` drill wants.
 *
 * Two rather than one, and the second is the lesson: it cannot happen until the
 * gun has reloaded, so waiting out the ring around the sight is something the
 * player does rather than reads. `stepTrigger` is the gate, on both sides, and
 * it is not negotiable from here.
 */
export const TUTORIAL_SHOTS = 2;
/**
 * Seconds between the learner's **first shot of the run** and the duelling
 * enemy arriving.
 *
 * The enemy used to appear the instant the `fire` drill was satisfied, which
 * meant it arrived on the tick of the second shot — a tank materialising as a
 * consequence of pulling the trigger, with no gap to read it in. Five seconds
 * makes the sequence *fire, then contact* rather than *fire and contact*, and it
 * gives the drill something to say while the yard is still empty.
 *
 * Anchored to the **first** shot rather than to the last, so the wait is a
 * property of the run and not of how long the player took over their second
 * volley. Both shots are comfortably inside it: `TUTORIAL_SHOTS` is 2 and the
 * recruit gun reloads in well under five seconds, so in practice this is the
 * binding constraint and the drill ends on the clock rather than on the count.
 *
 * It is a **stored tick and a subtraction**, never a countdown: the mission
 * keeps the tick of the first shot, puts the arrival tick on the snapshot as
 * `duelAtTick`, and every reading of "how long left" is that minus the current
 * tick. So a client that joined mid-wait — or reconnected — computes the same
 * arrival as everybody else, the way a crate's descent and a dropped core's
 * return timer already do.
 */
export const TUTORIAL_DUEL_DELAY_SECONDS = 5;
/** The same delay in ticks, so both sides derive one arrival. */
export const TUTORIAL_DUEL_DELAY_TICKS = Math.round(TUTORIAL_DUEL_DELAY_SECONDS * TICK_RATE);
/** Metres in front of the player the `duel` drill puts its single enemy. */
export const TUTORIAL_DUEL_RANGE = 45;
/**
 * Metres from the learner the `supply` drill lands its crate.
 *
 * Far enough that collecting it is a drive rather than a nudge, and derived from
 * the player rather than fixed on the map for the reason `TUTORIAL_DUEL_RANGE`
 * is: the yard has no bases, so `pickRingSpawn` can put a joining tank anywhere
 * on a twelve-point ellipse and the duel can end anywhere at all. A fixed point
 * would be five metres away on one run and a hundred and thirty on the next.
 *
 * Aimed *inward*, toward the middle of the yard, which is what keeps it inside
 * the arena walls at any starting position without a bounds check.
 */
export const TUTORIAL_SUPPLY_RANGE = 55;
/** Friendly AI tanks that arrive for the final drill. */
export const TUTORIAL_ALLIES = 2;
/** Enemy tanks in the final group. */
export const TUTORIAL_ASSAULT_COUNT = 3;

/**
 * What the tutorial is currently asking for.
 *
 * Named numbers rather than an opaque progress array, because this snapshot is
 * read by a person as often as by a component: a stage that will not advance is
 * diagnosed by looking at which of these is not climbing. It costs nothing worth
 * counting — the tutorial room holds exactly one human by construction.
 *
 * Every figure is **since the current stage began**, so a player who drove three
 * hundred metres working out the throttle still drives `TUTORIAL_DRIVE_METRES`
 * for the drill that asks for it.
 */
export interface TutorialSnapshot {
  kind: 'tutorial';
  stage: TutorialStageId;
  /** 1-based, for "Step 3 of 7". */
  step: number;
  steps: number;
  /** Metres driven during this stage. */
  metres: number;
  /** Radians of hull rotation during this stage, summed without sign. */
  hullRadians: number;
  /** Radians of turret traverse during this stage, summed without sign. */
  turretRadians: number;
  /** Volleys fired during this stage. */
  shots: number;
  /** Enemy tanks still standing. The `duel` and `assault` drills end on zero. */
  enemiesLeft: number;
  /** Friendly AI tanks still standing. */
  alliesLeft: number;
  /** Crates claimed during this stage. The `supply` drill ends on one. */
  crates: number;
  /**
   * The tick the duelling enemy is due, while the `fire` drill is waiting it out.
   *
   * A stored tick rather than a remaining count, so "in 3 s" is a subtraction
   * against `missionTick` on whichever side is asking — see
   * `TUTORIAL_DUEL_DELAY_SECONDS`. Absent before the first shot (there is nothing
   * to be due yet) and from `duel` onward (it has arrived).
   */
  duelAtTick?: number;
  /** Metres from the player to the crate, while there is one on the yard. */
  supplyDistance?: number;
  /**
   * Where the crate is, for the ring on the ground, the arrow above the sight
   * and the marker on the scope.
   *
   * Present only during `supply`. It restates a position the client can already
   * see — the crate rides on the snapshot's own `airdrops` like any other — and
   * that restatement is deliberate: it says *this* crate is the objective, which
   * is a fact about the drill rather than about the crate, and it is what the
   * three existing objective marks are drawn from. `radius` is the pickup
   * radius, so the ring on the ground is the rule rather than a hint about it.
   */
  supply?: { x: number; z: number; radius: number };
}

// Capture the flag.
/** Captures the raiding side needs. */
export const CTF_CAPTURES_TO_WIN = 3;
/** Metres: carrying the core inside this of the uplink captures it. */
export const CTF_CAPTURE_RADIUS = 12;
/** Metres: grabbing a core at its post, or a red tank returning a dropped one. */
export const CTF_PICKUP_RADIUS = 6;
/** A dropped core walks itself home after this long untouched. */
export const CTF_FLAG_RETURN_SECONDS = 30;
/** The same interval in ticks, so both sides derive the identical countdown. */
export const CTF_FLAG_RETURN_TICKS = Math.round(CTF_FLAG_RETURN_SECONDS * TICK_RATE);
/** Lives the human side gets per human, pooled. */
export const CTF_LIVES_PER_HUMAN = 4;
/** Seconds a dead raider waits before revival, while lives remain. */
export const CTF_REVIVE_SECONDS = 10;
/** Seconds a dead defender waits. A raid, not a war of attrition. */
export const CTF_DEFENDER_RESPAWN_SECONDS = 12;

// Wave defence.
/** Waves to survive. */
export const WAVE_COUNT = 8;
/** Seconds of quiet between a wave clearing and the next arriving. */
export const WAVE_INTERMISSION_SECONDS = 12;
/** The same interval in ticks. */
export const WAVE_INTERMISSION_TICKS = Math.round(WAVE_INTERMISSION_SECONDS * TICK_RATE);
/** Ticks after the first human arrives before wave one spawns. */
export const FIRST_WAVE_DELAY_TICKS = 10 * TICK_RATE;

// Escort.
/**
 * Metres: the convoy stops while any living enemy is this close.
 *
 * **Seven, down from forty-five, and the change is larger than the number
 * looks** — the old radius was doing a second job nobody had written down.
 * `EscortMission` used to serialise its ambushes *through* it: an unresolved
 * ambush was by definition inside 45 m, so the convoy was stopped and could
 * not reach the next leg's trigger, and the fights therefore arrived one at a
 * time because the geometry said so. At 7 m the convoy drives through a live
 * ambush, which is the intent — it is a supply column under escort, not a
 * vehicle that parks whenever anything hostile is on the same field — and the
 * serialisation had to be rebuilt as a thing that is actually stated. It is
 * `EscortMission.reinforce`'s cap and clock, and it is the *only* gate on how
 * many enemies exist now. Do not reintroduce a proximity-triggered burst
 * beside it.
 *
 * Seven is a little over two hull lengths: close enough that a tank standing
 * in the road stops the column, far enough that a wreck it is squeezing past
 * does not.
 */
export const CONVOY_HALT_RADIUS = 7;
/**
 * Metres: the convoy only rolls while one of its escorts is this close.
 *
 * **This is what paces the mission, and it replaced a clock doing the job
 * badly.** The column drives the whole 420 m road in 48 seconds if nothing
 * stops it, which is shorter than two reinforcement intervals — so with the
 * halt radius down at 7 m the run was over before enough enemies existed to
 * threaten anything (measured: 58 s, three tanks dealt, the convoy finishing
 * at full health having never been hit). Tuning the spawn clock against that
 * would have been calibrating a rate against a duration the player does not
 * control.
 *
 * Making the column wait for its escort inverts that: the mission takes as
 * long as the player takes, so a player who stops to fight is *paid* in road
 * they have not had to cover under fire, and one who runs ahead finds the
 * thing they are guarding sitting in the open behind them. It is also what an
 * escort **is** — the vehicle is not going anywhere without you.
 *
 * **A human, not any friendly.** The AI escort detail cannot release it and
 * cannot drive it, which is the same answer `CONVOY_RELEASE_RADIUS` already
 * gives one rule along: a column that trundles home behind two bots while the
 * player is elsewhere is not being escorted by the player, and the pacing this
 * exists for would go straight back. Change `escortNearby` if that is ever
 * wanted — it is one filter.
 *
 * Thirteen metres — a **radius**, so 26 m of ground across. Ten was the first
 * figure and it was tight enough that ordinary driving beside the column kept
 * dropping out of it; thirteen is still close enough to be a position rather
 * than a postcode. It is wider than `CONVOY_HALT_RADIUS` on purpose: a player
 * parked beside the column is inside both, so the two rules never fight over
 * one tank.
 */
export const CONVOY_ESCORT_RADIUS = 13;
/**
 * Ticks since the last hit for which the convoy still counts as under attack.
 *
 * The second half of the halt rule, and the half that carries it: at 7 m a
 * column would otherwise roll placidly on while being shelled from a hedgerow
 * eighty metres off. Three seconds — long enough that a reload between rounds
 * does not read as the shooting having stopped, short enough that one stray
 * hit does not park the mission.
 *
 * A **stored tick compared against the current one**, never a counter
 * something decrements, exactly like the revive clock beside it. See
 * `EscortMission.step`.
 */
export const CONVOY_ATTACK_MEMORY_TICKS = Math.round(3 * TICK_RATE);
/** Metres: reaching this of the last route point wins the mission. */
export const CONVOY_ARRIVE_RADIUS = 10;
/** Metres: reaching this of the current waypoint advances the leg. */
export const CONVOY_WAYPOINT_RADIUS = 6;
/** Fraction of max health the convoy recovers at each waypoint. */
export const CONVOY_LEG_HEAL_FRACTION = 0.12;
/** Seconds a dead escort waits before being put back on the road. */
export const ESCORT_REVIVE_SECONDS = 8;
/** Metres behind the convoy a revived escort is placed. */
export const ESCORT_REVIVE_TRAIL = 27;

/**
 * What the supply column is doing, as one word on the wire.
 *
 * Four states rather than the boolean this replaced, because the label
 * floating over the vehicle has to answer *why* it is standing still — a
 * column stopped because a tank is in the road and one stopped because it is
 * being shelled are different pieces of information, and "halted" is the half
 * of it the player can already see.
 *
 * **An enum, resolved into words on the client.** The same rule
 * `Nameplate.titleId` follows one surface along: the wire carries an id and
 * `GameHud.vue` owns the wording, so an e2e case asserting the column is under
 * attack compares the state the server decided rather than a rendered string
 * somebody is free to retitle.
 *
 * - `waiting` — no escort within `CONVOY_ESCORT_RADIUS`. It is not going
 *   anywhere without one, so this covers both the column parked on the start
 *   pad before anybody has arrived *and* a column that has been left behind
 *   mid-route. Deliberately one state and not two: the player is told the same
 *   thing in both ("come back"), and the difference between them is a fact
 *   about the mission's history rather than about the vehicle.
 * - `rolling` — driving the leg. The only state in which it moves at all.
 * - `enemy-near` — a living enemy inside `CONVOY_HALT_RADIUS`. Stopped.
 * - `under-attack` — hit within `CONVOY_ATTACK_MEMORY_TICKS`. Stopped.
 *
 * Ranked in reverse of that list when more than one applies, which is most of
 * the time: being shot at outranks a tank in the road (a shooter is usually
 * outside 7 m anyway), and both outrank a missing escort, because the first
 * two are things the player must deal with *now* and the third is a standing
 * condition they can already see.
 */
export type ConvoyStatus = 'waiting' | 'rolling' | 'enemy-near' | 'under-attack';

/**
 * Whether that status means the column has stopped.
 *
 * Stated as "anything but `rolling`" rather than as a list of the stopping
 * states, which is both the honest definition and the one that cannot rot: a
 * fifth reason to stop, added later, is halted by default instead of silently
 * becoming a fifth way to drive. It got that way by being wrong in exactly the
 * other direction — `waiting` was outside the list when the escort-proximity
 * rule landed, so a column with nobody near it reported *stopped* to the HUD
 * and *drive on* to the driver.
 *
 * One function rather than the comparison written out at each of the three
 * places that ask — the driver, the mission strip and the mission's own
 * `inputFor`.
 */
export function convoyHalted(status: ConvoyStatus): boolean {
  return status !== 'rolling';
}

/** Where the core is. */
export type FlagState = 'home' | 'carried' | 'dropped';

/**
 * The core, as the wire describes it.
 *
 * Carried in full every tick rather than as take/drop events, for exactly the
 * reason `airdrops` is: a client that joined mid-raid has had no events at all,
 * and a dropped event lost to a hiccup would leave one client showing a core
 * that is not there. The events beside it are for the feed and the audio cue,
 * never the source of truth.
 */
export interface FlagSnapshot {
  state: FlagState;
  /**
   * Where the core object is. Meaningful when `home` or `dropped`; while
   * `carried` the client draws it on the carrier and these are a fallback.
   */
  x: number;
  z: number;
  /** Present only while `carried`. */
  carrierId?: string;
  /**
   * Present only while `dropped`. The auto-return countdown is
   * `droppedAtTick + CTF_FLAG_RETURN_TICKS - tick` — derived on read, never a
   * number counted down on the wire, the same rule a crate's descent follows.
   */
  droppedAtTick?: number;
}

/**
 * What a mission is currently asking of the player.
 *
 * A discriminated union rather than one flat object with everything optional,
 * so a wave field cannot be read during an escort. Present only in a mission
 * mode — see `ArenaSnapshot.mission`.
 */
export type MissionSnapshot =
  | TutorialSnapshot
  | {
      kind: 'escort';
      /**
       * The convoy's `playerId`. Everything else about it — position, health,
       * whether it is alive — rides on its ordinary `PlayerSnapshot`, because
       * it is an ordinary tank.
       */
      convoyId: string;
      /** 0-based index of the waypoint being driven to. */
      leg: number;
      /** Number of waypoints in the route, so the HUD can say "LEG 3 / 6". */
      legs: number;
      /**
       * What the column is doing — the whole of it, in one field.
       *
       * This replaced a `halted: boolean`, and the replacement rather than an
       * addition is the point. The label floating over the vehicle has to say
       * *why* it has stopped (see `ConvoyStatus`), so a second field beside
       * the boolean would have been two statements about one thing with a tick
       * in which they could disagree — and the one the player believes is
       * whichever is on screen. Halted is now derived: it is exactly
       * `convoyHalted(status)`, which both the mission strip and the driver
       * read rather than each deciding for itself.
       */
      convoy: ConvoyStatus;
    }
  | {
      kind: 'ctf';
      captures: number;
      flag: FlagSnapshot;
      /** Shared respawn lives left across the whole human side. */
      livesLeft: number;
    }
  | {
      kind: 'wave-defence';
      /** 1-based. 0 before the first wave spawns. */
      wave: number;
      /**
       * Tick the next wave spawns. Present only during a lull; absent while a
       * wave is being fought. The countdown is derived from it.
       */
      nextWaveAtTick?: number;
    };

/**
 * How a battle ended.
 *
 * Absent until it does. Once set it stays set: the arena stops simulating and
 * waits for people to leave rather than being torn out from under them.
 *
 * A mission reuses it unchanged: `winner` is `MISSION_TEAM` for a mission
 * completed and `'red'` for one failed, so the freeze, the refused joins and
 * the retired room all work with no mission-shaped exception. A deathmatch
 * sets it too, since the mode gained an end — the timer or the kill target
 * below — with `winner` resolved by total team kills, `null` on a tie.
 */
export interface MatchOutcome {
  /** The side that took the match, or `null` if the scores could not separate. */
  winner: TeamId | null;
  /** Server tick the battle was decided on. */
  decidedAtTick: number;
}

/**
 * One player's tallies at the moment a battle was decided.
 *
 * The in-memory figures `ArenaPlayer` has been accumulating all match — the same
 * ones `COMBAT_STATS_SINK` is handed on the way out — read once and put on the
 * `battleOver` event. Nothing here is persisted by being sent, and sending it
 * changes nothing about when it is: the flush is still one write per player per
 * visit, on disconnect.
 *
 * **Kills and deaths are absent on purpose.** They are already on every
 * `PlayerSnapshot`, the last one a decided arena sends included, and the client
 * joins this to that roster by `playerId`. See the note on `battleOver.summary`.
 *
 * **Everything on it is final**, the reward included. A decided arena freezes,
 * so nobody drives another metre — and the payout clock stops with it: seconds
 * are credited only up to `decidedAtTick`, so reading the result screen earns
 * nothing and the figures shown are exactly the base figures banked on leave.
 * That cap is what made carrying a reward here honest at all; before it, any
 * figure drawn would have been understated by however long somebody looked at
 * the screen.
 */
export interface BattleTally {
  playerId: string;
  /** Damage dealt to other tanks, after their armour. Never self-inflicted. */
  damageDealt: number;
  /** Airdrop crates claimed. */
  cratesCollected: number;
  /** Metres driven. Final, because a decided arena stops simulating. */
  distanceDriven: number;
  /**
   * What this battle pays, before boosts.
   *
   * Computed on the deciding tick through the same pure `rewardBreakdown` the
   * banking in `closeSession` runs, so the screen and the ledger agree on the
   * base figures by construction — boosts are applied at banking, where the
   * wallet is, and only ever raise them. A bot gets one too and it is
   * discarded with its kills; tailoring the list per socket would be the
   * first `if (bot)` on this path.
   */
  reward: BattleReward;
}

/**
 * One player's payout for one battle, itemised, before boosts.
 *
 * All four sources are credit amounts, not counts — the counts are on the
 * roster and on the tally beside this. `credits` is their sum and `xp` is the
 * same facts through the XP curve; both are the *base* figures, scaled by any
 * loot/XP boost when they are banked on disconnect.
 */
/**
 * What one closed sortie paid, as the client's reward window reads it.
 *
 * Sent once, to one socket, in reply to an explicit `leave` — never broadcast and
 * never on a snapshot, because it is a fact about one player's account rather than
 * about the battle. Every figure is the server's: the credits and XP are what
 * `closeSession` banked, boosts included, and `forfeited` is why they are **zero**
 * — a sortie left before the battle was decided pays nothing (see the walking-out
 * note in `garage/economy.ts`). The client renders these and computes none of them.
 *
 * On the wire rather than in the gateway because the browser reads it, and a
 * second copy of a wire shape is the drift this file exists to prevent.
 */
export interface SortieSettlement {
  credits: number;
  xp: number;
  /**
   * True when the player left before the battle was decided — in which case
   * `credits` and `xp` are both 0. Still carried rather than inferred from the
   * pair being zero: a fought sortie that scored nothing and an abandoned one are
   * different things to say, and the receipt says which.
   */
  forfeited: boolean;
  kills: number;
  deaths: number;
}

export interface BattleReward {
  /** Turning up, time in the arena (to the deciding tick) and metres driven. */
  participation: number;
  /** Kill credits. */
  kills: number;
  /** Damage credits. */
  damage: number;
  /** The win bonus, or 0. */
  win: number;
  /** Total base credits. */
  credits: number;
  /** Total base experience. */
  xp: number;
}

/**
 * Rounds a side must take to win a team battle outright.
 *
 * The scoreboard, the "match point" line and `checkRoundOver`'s early-exit all
 * derive from it. Shared rather than server-only because the client draws the
 * score, and the pips, against it.
 *
 * It used to be 8 with no cap, which made a team battle a race with no
 * ceiling — a side that kept trading draws could in principle play for ever.
 * It is now read together with `MAX_ROUNDS` below: first to `ROUNDS_TO_WIN`
 * out of a match capped at `MAX_ROUNDS`, best-of-six.
 */
export const ROUNDS_TO_WIN = 3;

/**
 * The round cap the format is played to before a tie forces a decisive round.
 *
 * A team battle is first to `ROUNDS_TO_WIN` (3) — a race that, without draws,
 * always finishes inside five rounds. Draws (a mutual wipe-out scores nobody)
 * are what make a cap necessary at all: enough of them and neither side ever
 * reaches 3. Six is that cap, chosen so "best of six" is the honest name for
 * the format rather than a round number picked for its own sake.
 *
 * **Reaching the cap does not mean an undecided match.** `checkRoundOver`
 * reads this only once a round has ended without either side hitting
 * `ROUNDS_TO_WIN`: if the score differs, the side ahead takes the match
 * outright — the ordinary reading of "best of six," where nobody demands a
 * literal 3-0. If the score is still level, the cap stops applying rather
 * than the match stopping: round seven, and if it comes to it every round
 * after, is sudden death — no cap, no draw left un-replayed — until one round
 * actually has a winner. That round decides the match. A team battle must
 * never freeze on a tie; this constant is what "if tied, then the last
 * battle decides it" means in code.
 */
export const MAX_ROUNDS = 6;

/**
 * Seconds between a round ending and the next one starting.
 *
 * Not slack. The round ends on the tick a side's last tank dies, and without a
 * pause the survivors are teleported back to their base mid-explosion — nobody
 * gets to see that they won. It is also when the score is worth reading.
 */
export const ROUND_INTERMISSION_SECONDS = 5;

/** The same interval in ticks. Derived, so both sides agree exactly. */
export const ROUND_INTERMISSION_TICKS = Math.round(ROUND_INTERMISSION_SECONDS * TICK_RATE);

/**
 * Seconds an arena is frozen before its first tank can move.
 *
 * Long enough to find your bearings — which tank you are in, where your side is,
 * which way the enemy base is — before anybody can shoot at you. A match starting
 * the instant a player lands rewards whoever happened to be looking the right way,
 * and gives a player no chance to see the map before it's already live.
 *
 * **Every mode, guarded the same way team battle's round one always was: on the
 * arena having been empty.** `Arena.add()` sets this only when `humanCount` was
 * zero right before the join — the moment a match is genuinely starting, not the
 * moment any one player happens to arrive. A second human joining a room already
 * under way does **not** freeze it: that would be freezing the match *for* the
 * people already playing it, which is exactly what the guard exists to prevent.
 * A deathmatch is ordinarily a permanent room people join and leave continuously,
 * but it still has that one moment — the transition from nobody in it to somebody
 * in it — and that is the moment this covers, in every mode including it.
 *
 * The client renders the last two seconds of this differently — "TO BATTLE!" in
 * the game's own accent colour rather than the plain countdown — but that split
 * is purely a client-side reading of the same number; nothing on the wire marks
 * it, and there is no second freeze after it.
 */
export const ROUND_START_SECONDS = 10;

/** The same interval in ticks. Derived, for the reason above. */
export const ROUND_START_TICKS = Math.round(ROUND_START_SECONDS * TICK_RATE);

/** Rounds taken, per side. */
export type MatchScore = Readonly<Record<TeamId, number>>;

// ─── Deathmatch end conditions ───────────────────────────────────────────────

/**
 * How long a deathmatch runs, from the moment its first human joins.
 *
 * A deathmatch used to be the mode with no end; it now finishes so the result
 * screen — and the win bonus — exist in it. Shared because the client draws
 * the match clock against it, as a subtraction from `ArenaSnapshot.endsAtTick`;
 * nothing anywhere counts down.
 *
 * The tutorial is a deathmatch on the drill yard and is exempt (`endless` at
 * arena construction): a learner must not be interrupted at ten minutes by a
 * result screen for a room that banks nothing.
 */
export const DEATHMATCH_DURATION_SECONDS = 600;

/** The same span in ticks. Derived, so both sides agree exactly. */
export const DEATHMATCH_DURATION_TICKS = DEATHMATCH_DURATION_SECONDS * TICK_RATE;

/**
 * Any tank — human or AI — reaching this many kills ends the deathmatch on
 * that tick, ahead of the timer. The winning side is the one with more total
 * kills at the end, whichever condition fired; equal totals are a draw.
 */
export const DEATHMATCH_KILL_TARGET = 30;

// ─── Team orders ─────────────────────────────────────────────────────────────

/**
 * What a player has told their side's AI tanks to do.
 *
 * `'attack'` is the default and means *exactly* what an uncommanded team does:
 * every unit fights on its own initiative. The server resolves it to no
 * formation at all, so a team under it runs the code path a team has always run
 * — which is why a player who never touches the number row sees no change.
 *
 * Only AI tanks obey. A human is never ordered anywhere by another human.
 */
/**
 * Whether a player may command their side's AI tanks at all.
 *
 * **Off.** `1`/`2`/`3` and `4`/`5`/`6` do nothing, the HUD's orders panel is not
 * drawn, and `Arena.applyOrder` refuses to store an order however a client asks
 * — so a hand-rolled one cannot command anything either. Every AI tank on a
 * player's side therefore drives the way an uncommanded one always has.
 *
 * ── Why this is one constant rather than a deletion ──────────────────────────
 *
 * It was asked for as "for now", so re-enabling is this line and nothing else.
 * The whole mechanism stays live code with its tests: `commands.ts` and its
 * formation maths, `stepBot`'s formation branch, the wire's `order` / `orderSeq`
 * fields, `orderFromCode`, the snapshot's `orders` key and the HUD's panel are
 * all intact and all still tested. Ripping them out would be a large diff to
 * write, a larger one to revert, and it would take the *mission* machinery with
 * it — see below.
 *
 * ── What this does NOT disable, and the distinction is load-bearing ──────────
 *
 * **A mission anchor is not a team order.** CTF's posture table, wave defence's
 * garrison and the escort all drive their bots through `FormationAnchor` and
 * `MissionRuntime.anchorFor`, which is the *same seam* the number keys used to
 * reach. That path is untouched and must stay untouched: a mission commanding
 * its own tanks is the mission working, not a player commanding units. If
 * flipping this constant ever appears to change how CTF's defenders converge on
 * a carrier, the wrong thing has been disabled.
 *
 * ── The trap this constant is under ─────────────────────────────────────────
 *
 * `RUNNING_GEAR_ENABLED` was switched off while the belts were being re-fitted
 * and then never switched back, and every rig stayed green for weeks because the
 * rigs built their own belts — a kill switch with no test that notices it is a
 * kill switch that gets left on. So this one is asserted by name in
 * `arena.test.ts` and `input.test.ts`: the tests state *which* state it is in, so
 * flipping it fails loudly and deliberately rather than silently changing the
 * game. Turning commands back on means flipping this line and updating those
 * assertions in the same commit, which is the point.
 */
export const TEAM_ORDERS_ENABLED = false;

export type TeamOrder = 'attack' | 'follow' | 'hold';

/** How tightly a commanded formation packs together. */
export type FormationDensity = 'close' | 'normal' | 'spread';

/** The wire codes for `TeamOrder`. 0 is reserved for "unchanged". */
export type TeamOrderCode = 0 | 1 | 2 | 3;
/** The wire codes for `FormationDensity`. 0 is reserved for "unchanged". */
export type FormationDensityCode = 0 | 1 | 2 | 3;

/**
 * Order to code.
 *
 * The numbers are the keys the player presses, which is not decoration: someone
 * reading a packet capture and someone reading the controls list are looking at
 * the same three numbers.
 */
export const ORDER_CODES: Readonly<Record<TeamOrder, 1 | 2 | 3>> = {
  attack: 1,
  follow: 2,
  hold: 3,
};

/** Density to code. 4 / 5 / 6 on the keyboard — the same row as the orders. */
export const DENSITY_CODES: Readonly<Record<FormationDensity, 1 | 2 | 3>> = {
  close: 1,
  normal: 2,
  spread: 3,
};

/** A side's standing order, as the HUD is told it. */
export interface TeamOrders {
  order: TeamOrder;
  density: FormationDensity;
  /**
   * Who gave it, for the HUD to name. Absent once they have left the arena.
   *
   * A `follow` order with no commander left in the arena reverts to `'attack'`
   * server-side rather than escorting a ghost.
   */
  commanderId?: string;
}

export interface PlayerSnapshot extends TankState {
  id: string;
  nickname: string;
  /** Last input sequence the server had applied for this player at this tick. */
  lastSeq: number;
  /** Current hit points. 0 while destroyed. */
  health: number;
  /** This player's maximum, from their TankSpec — remote health bars need it. */
  maxHealth: number;
  /** False between destruction and respawn. Dead tanks are not drawn or hit. */
  alive: boolean;
  kills: number;
  deaths: number;
  /** Which side this tank fights on. */
  team: TeamId;
  /**
   * Airdrop buffs currently applying, and the tick each stops.
   *
   * On every player rather than just the local one: a buffed enemy is a fact
   * the other side needs — it is the difference between chasing and running —
   * and the client already has to draw remote tanks from this snapshot.
   */
  buffs: ActiveBuff[];
  /**
   * True when an AI is driving.
   *
   * A bot is **not** a different kind of entity — it holds a slot in the arena,
   * carries a `TankSpec` from the catalogue and is advanced by the same
   * `stepTank` as everybody else; only the source of its `InputCommand` differs.
   * This flag exists so the client can label it, and for nothing else: remote
   * interpolation, hit detection, damage, death and respawn all treat it exactly
   * as a human, which is the entire point of feeding the AI in as input.
   */
  isBot: boolean;
}

/**
 * A shell in flight, as the wire sees it.
 *
 * Shells are not interpolated from snapshots: they travel in a straight line at
 * a constant speed, so the client is told once where one started and simulates
 * the rest itself. `spawnTick` is what lets a client that joined mid-flight (or
 * dropped a packet) place an existing shell correctly.
 */
export interface ShellSnapshot {
  id: number;
  ownerId: string;
  /** Muzzle position the shell started from. */
  originX: number;
  originZ: number;
  /** Unit direction of travel. */
  dirX: number;
  dirZ: number;
  speed: number;
  /** Server tick the shell was created on. */
  spawnTick: number;
  /** Metres it travels before expiring. */
  range: number;
  /**
   * Peak height of the arc to draw this shell along, in metres.
   *
   * Cosmetic and cosmetic only — the simulation is flat, and a mortar's arc is
   * expressed to it as `armDistance` (the round is over everyone's heads until
   * it has travelled that far). Absent or 0 means a flat tracer.
   */
  arcHeight?: number;
  /** Area-damage radius on detonation, so the client can size the burst. */
  blastRadius?: number;
  /**
   * Wall reflections this shell has left. A client drawing a ricochet needs it
   * to know the tracer will turn rather than stop.
   */
  bounces?: number;
}

/** Why a shell stopped existing. Drives which impact effect the client plays. */
export type ShellEndReason = 'hit' | 'expired' | 'wall';

/**
 * Things that happened during one tick, delivered alongside the snapshot.
 *
 * They ride *in* the snapshot rather than as their own emits so ordering with
 * the authoritative state is guaranteed: a client can never see the health drop
 * before the impact that caused it.
 */
export type BattleEvent =
  | { type: 'fired'; shell: ShellSnapshot }
  | {
      type: 'impact';
      shellId: number;
      ownerId: string;
      x: number;
      z: number;
      reason: ShellEndReason;
      /** Present when the shell hit a tank. */
      victimId?: string;
      /** Damage actually applied, after the victim's armour. */
      damage?: number;
    }
  | {
      type: 'killed';
      victimId: string;
      victimName: string;
      killerId: string;
      killerName: string;
      x: number;
      z: number;
    }
  | {
      /**
       * An area-damage detonation.
       *
       * Sent *in addition to* the shell's own `impact`, never instead of it, so
       * a client that knows nothing about blasts still removes the tracer and
       * still draws a burst. Everything here is a flourish on top: the radius to
       * ring, and who was caught by the splash rather than by the shell itself.
       */
      type: 'blast';
      shellId: number;
      ownerId: string;
      x: number;
      z: number;
      radius: number;
      /** Splash victims only. The direct hit is reported by `impact`. */
      victims: { id: string; damage: number }[];
    }
  | {
      /**
       * What the garage handed this player: which hull and turret they are
       * driving, and which title they wear on their name.
       *
       * Emitted when someone joins — for them *and* for everyone already in the
       * arena, so a late joiner learns the whole field — and again whenever a
       * loadout changes mid-session. Deliberately an event rather than three
       * more strings on every `PlayerSnapshot`: all three change a handful of
       * times a session and the snapshot goes out thirty times a second to
       * every socket in the room, so the per-tick cost of this is exactly zero
       * and `arena-cost.test.ts` measures it as zero.
       *
       * **This is the roster announcement**, and that is why the title rides on
       * it rather than on an event of its own. A second event would need its own
       * copy of the announce-everyone-to-the-newcomer loop, its own place in
       * `addBot`, in `updateSpec`, in the takeover and in the round restore —
       * five sites to keep in step for one string. Every site here goes through
       * `Arena.loadoutEvent`, so there is one constructor and a future sixth
       * caller cannot forget a field.
       *
       * The client turns the ids into mesh paths through `TURRET_PROFILES` /
       * `HULL_PROFILES` in `weapons.ts`, and the title into a word, a colour and
       * a treatment through `TITLE_PROFILES` in `titles.ts`.
       */
      type: 'loadout';
      playerId: string;
      hullId: string;
      turretId: string;
      /**
       * The title this player wears, or absent for none — which is most people,
       * and is what a bot always is.
       *
       * Absent rather than `null` so an arena in which nobody has bought one is
       * byte-for-byte what it was before titles existed. Resolved server-side
       * from the account: a client never asserts its own, and an id the client
       * does not recognise draws as no title rather than as the literal string.
       */
      titleId?: string;
      /**
       * The livery this player has equipped, or absent for the issued coat.
       *
       * Absent rather than the issued coat's id, for the same reason `titleId` is
       * absent rather than null: an arena in which nobody has bought a paint is
       * byte-for-byte what it was before liveries existed, and `arena-cost`
       * keeps measuring zero. `liveryFor` in `shared/liveries.ts` is total, so
       * the missing field and an unrecognised id both resolve to the stock look
       * on the client rather than to a decision the browser has to make.
       *
       * **It rides here rather than on the snapshot**, and that is the same
       * argument the hull and the title make: a paint changes a handful of times
       * a session and the snapshot goes out thirty times a second. It also has
       * to ride on *this* event specifically, because this is the roster
       * announcement — the one that tells a late joiner about everybody already
       * in the room. A player whose paint arrived by any other route would be
       * correctly painted for the people who watched them join and stock for
       * everybody who arrived afterwards.
       */
      paintId?: string;
    }
  | { type: 'respawned'; playerId: string; state: TankState }
  | {
      /**
       * A player whose tank was destroyed has taken over one of their side's AI
       * tanks. Team battle only.
       *
       * Deliberately its own event rather than a `respawned` with a surprising
       * position. Three things change at once — where the player is, which tank
       * they are driving, and the fact that one of their side's AI slots has
       * ceased to exist — and a client that treated it as a respawn would keep
       * drawing the bot it just absorbed. The accompanying `loadout` event
       * carries the new hull and turret; this carries the rest.
       */
      type: 'tookOver';
      playerId: string;
      /** The AI slot that has been absorbed. Clients must stop drawing it. */
      fromBotId: string;
      /** Its call sign, for the feed: "Ana takes over Sable". */
      fromBotName: string;
      state: TankState;
      health: number;
      maxHealth: number;
    }
  | {
      /**
       * A round ended. Team battle only.
       *
       * Separate from `battleOver` because they are different facts and the
       * client does different things with them: a round ending is a scoreboard
       * and a countdown to the next one, the battle ending is the end. Most
       * rounds raise this and never raise the other.
       */
      type: 'roundOver';
      /** The side that took the round, or `null` if both were wiped together. */
      winner: TeamId | null;
      /** The round that just finished, 1-based. */
      round: number;
      /** The score *after* this round. */
      score: MatchScore;
      /** Tick the next round starts on. */
      nextRoundAtTick: number;
    }
  | {
      /**
       * The battle is decided.
       *
       * **Not team battle only** — that is what the line here used to say, and a
       * mission raises it too, through `Arena.finishMission`. A mission reuses
       * `outcome` and this event unchanged, which is the whole point of a mission
       * being a `MatchMode`; the only difference is the wording, and the wording is
       * the client's. A deathmatch raises it too now, from its timer or its kill
       * target — see `DEATHMATCH_DURATION_TICKS` and `DEATHMATCH_KILL_TARGET`.
       *
       * The arena stops simulating from this tick; it is not torn down, so
       * players leave under their own power rather than being disconnected.
       */
      type: 'battleOver';
      winner: TeamId | null;
      /** Why it ended, for the banner: which side ran out of tanks. */
      loser: TeamId | null;
      /**
       * What everybody in the arena did, for the end-of-battle summary.
       *
       * **This is the cheapest place in the protocol to put a tally**, and that
       * is why it is here rather than on `PlayerSnapshot` or even on `loadout`.
       * A `loadout` event is raised once per player per join, per bot dealt in,
       * per takeover and per round restore; this event is raised **once in the
       * whole life of an arena**. One broadcast, ~130 B a player with the reward
       * block, and then the
       * arena freezes. On `PlayerSnapshot` the same three numbers would be
       * ~30 B × 30 players × 30 Hz ≈ 27 KB/s of room egress for the whole match,
       * for figures nobody reads until it is over — the argument `titleId` makes,
       * with the numbers a little worse.
       *
       * **It deliberately restates nothing the snapshot already carries.** Kills
       * and deaths are on every `PlayerSnapshot`, including the last one a decided
       * arena sends, so the client joins on `playerId` and reads them from the
       * roster it is already holding. Sending them again would be a second
       * statement of one fact, and the two would differ on exactly the tick
       * somebody dies — the same reason the convoy's health is not its own field.
       *
       * Every player is included, bots as well, because the event is one
       * broadcast to a room and cannot be tailored per socket — and because a
       * bot's tallies are counted exactly like a human's right up to the moment
       * they are discarded, so excluding them would be the first `if (bot)` on
       * this path.
       */
      summary: BattleTally[];
    }
  | {
      /**
       * A player is out of the battle: their tank is gone and their side has no
       * AI tank left for them to take over. Team battle only.
       *
       * Distinct from `killed`, which every death raises. This is the one that
       * means "you are not coming back", and it is what the HUD hangs a
       * spectating state off.
       */
      type: 'eliminated';
      playerId: string;
      playerName: string;
      team: TeamId;
    }
  | {
      /** A crate has been released and is on its way down. */
      type: 'airdropSpawned';
      airdrop: AirdropSnapshot;
    }
  | {
      /** Somebody reached a crate first. */
      type: 'airdropClaimed';
      id: number;
      kind: AirdropKind;
      playerId: string;
      playerName: string;
      /** Tick the buff stops applying. Absent for instant crates. */
      expiresAtTick?: number;
      /** Health restored, for a repair crate. */
      healed?: number;
      /** Diamonds paid, for a loot crate. Only ever sent to the claimant. */

    }
  | {
      /** Nobody came for it. */
      type: 'airdropExpired';
      id: number;
    }
  // ── Missions ──
  // Every one of these is a *cue*: a kill-feed line, a sound, a marker flash.
  // None of them is the source of truth for anything, because a client that
  // joined mid-mission has had no events at all — the state it renders comes
  // from `ArenaSnapshot.mission`, which is carried in full. Same division the
  // airdrops keep: crates are reconciled from the snapshot and their events
  // only make a noise.
  | {
      /** A raider picked the core up, at its post or off the ground. */
      type: 'flagTaken';
      playerId: string;
    }
  | {
      /** The carrier died or left. */
      type: 'flagDropped';
      x: number;
      z: number;
      /** Tick it was dropped. The return countdown derives from this. */
      droppedAtTick: number;
    }
  | {
      /** Home again. `byId` absent means it timed out rather than being carried back. */
      type: 'flagReturned';
      byId?: string;
    }
  | {
      type: 'flagCaptured';
      playerId: string;
      /** Captures the raiders now hold, so the feed can say "2 / 3". */
      captures: number;
    }
  | {
      type: 'waveStarted';
      /** 1-based. */
      wave: number;
      /** How many tanks it brought. */
      count: number;
    }
  | {
      type: 'waveCleared';
      wave: number;
    }
  | {
      /** The convoy reached a waypoint. */
      type: 'convoyLeg';
      leg: number;
      /** Health it recovered for getting there. */
      healed: number;
    }
  | {
      /** Friendly AI tanks joined the mission — the garrison, or reinforcements. */
      type: 'alliesArrived';
      count: number;
      /** Living friendly bots after they arrived, so the feed can say how thin it is. */
      strength: number;
    };

export interface ArenaSnapshot {
  tick: number;
  /** Server clock in ms, used to place snapshots on the client's interpolation timeline. */
  serverTime: number;
  players: PlayerSnapshot[];
  /** Shells currently in flight. Empty most ticks. */
  shells: ShellSnapshot[];
  /**
   * Crates in the air or waiting on the ground.
   *
   * Carried in full rather than as spawn/despawn events, because it is a
   * handful of small objects and a client that joins mid-match has to see the
   * ones already falling. Height is not sent: it is a pure function of
   * `spawnTick`, so sending it would be a second source of truth to drift.
   */
  airdrops: AirdropSnapshot[];
  /** What happened on this tick. Empty most ticks. */
  events: BattleEvent[];
  /**
   * Rounds taken by each side, and which round is being played.
   *
   * **Team battle only** — absent for the whole of a deathmatch, so a
   * deathmatch snapshot is byte-for-byte what it was before rounds existed.
   * Carried on every snapshot rather than only on the `roundOver` event because
   * a player who joins mid-match has had no events at all, and "what is the
   * score" is the first thing they will ask.
   */
  score?: MatchScore;
  /** Which round is being played, 1-based. Team battle only. */
  round?: number;
  /**
   * Ticks until the next round starts, while a round is over and the next has
   * not begun. Absent while a round is actually being played.
   */
  intermissionTicks?: number;
  /**
   * The tick the arena unfreezes on, while it is counting in. Absent once it has.
   *
   * **Every mode**, unlike `score`/`round`/`intermissionTicks` above — a fresh
   * deathmatch or mission gets the same frozen count-in a team battle's first
   * round always has (see `ROUND_START_SECONDS`), so this is not folded into
   * that team-battle-only bundle. A deathmatch or mission snapshot that never
   * counts in (every join after the arena's first) carries no more fields than
   * it did before this existed.
   *
   * A **tick**, not a remaining count, for the reason every clock in this game is:
   * the client subtracts it from the snapshot's own `tick` and nothing anywhere
   * counts down, so a reconnect in the middle of the count-in derives the same
   * instant as everybody else. `intermissionTicks` above is the older shape and
   * gets away with it because the intermission window emits no snapshots at all —
   * one figure, sent once, and the client works from the `roundOver` event's
   * `nextRoundAtTick`. This window *does* broadcast, so it can carry the tick.
   */
  roundStartsAtTick?: number;
  /**
   * The standing order each side is under, when either side is under one.
   *
   * **Absent while nobody has given an order**, so an arena nobody is commanding
   * costs exactly what it cost before this existed. One entry per side rather
   * than a copy on every `PlayerSnapshot`: it is a single fact about a team, and
   * per-player it would be that fact repeated six times, thirty times a second,
   * forever — the same argument that made `loadout` an event rather than two
   * more strings on every player. It also makes the disagreeing state
   * unrepresentable: two tanks on one side cannot hold different opinions about
   * their own side's order.
   *
   * The client shows only its own side's. Both are carried because the snapshot
   * is one broadcast to the room; a side's posture is no more hidden than the
   * positions, health and buffs already in here.
   */
  orders?: Partial<Record<TeamId, TeamOrders>>;
  /**
   * How this battle ended, once it has.
   *
   * Present once any mode that ends has ended — a team battle at
   * `ROUNDS_TO_WIN`, a mission at its objective, a deathmatch at its timer or
   * kill target. Carried on the snapshot rather than only as an event because a
   * client that joined or reconnected after the fact still has to know the
   * battle is over — an event it missed would leave it showing a live HUD over a
   * frozen arena.
   */
  outcome?: MatchOutcome;
  /**
   * The tick this deathmatch ends on, once its clock has started.
   *
   * Deathmatch only, and absent until the room's first human joins — the clock
   * starts with them, and resets if the room empties and refills. A stored tick
   * rather than a seconds-left figure, per the standing rule: every countdown
   * on the client is a subtraction on read, so two clients can never disagree
   * about the clock by their frame rate. The kill target needs no field — it is
   * the shared `DEATHMATCH_KILL_TARGET`, and the kills are already on every
   * `PlayerSnapshot`.
   */
  endsAtTick?: number;
  /**
   * What the mission is currently asking of the player.
   *
   * **Absent for the whole of a deathmatch and a team battle**, so a PvP
   * snapshot is byte-for-byte what it was before missions existed — the same
   * promise `score`, `round` and `orders` make, and pinned the same way in
   * `arena-cost.test.ts`.
   *
   * Carried on every snapshot rather than only on the events that change it,
   * for the reason the crates are: a player who joins a raid in progress has
   * had no events at all, and "where is the core, and are we winning" is the
   * first thing they will ask. It is 44–128 bytes against a snapshot already
   * carrying 230 per tank.
   */
  mission?: MissionSnapshot;
}

/** A crate, as the wire describes it. */
export interface AirdropSnapshot {
  id: number;
  kind: AirdropKind;
  x: number;
  z: number;
  /** Tick it was released. Descent and expiry both derive from this. */
  spawnTick: number;
}

// ─── Arenas ──────────────────────────────────────────────────────────────────

/**
 * The quick-match arena for a given map.
 *
 * Quick match is **per map**, because an arena's map is fixed at construction —
 * it has to be, since the client resolves collision geometry from it and a room
 * that changed maps mid-match would have half its players predicting against a
 * building that is no longer there. So picking a map in the garage does not
 * reconfigure a room; it chooses which room to walk into.
 *
 * Derived on both sides from the same function rather than string-built twice.
 * The id being derived rather than random is what makes this work without five
 * idle simulations: a quick-match arena for an unpopular map is reaped when it
 * empties and lands the next player straight back into the same id.
 */
export function quickMatchIdFor(mapId: string, mode: MatchMode = DEFAULT_MATCH_MODE): string {
  // The mode is part of the id for exactly the reason the map is: an arena
  // cannot change either one mid-match, so two rooms are needed rather than one
  // room with a setting. The default mode is left out of the string so the
  // original quick-match id is unchanged — a deathmatch on the default map is
  // still `quick-<mapId>`, and nothing that already links to it breaks.
  const suffix = mode === DEFAULT_MATCH_MODE ? '' : `-${mode}`;
  return `quick-${mapId}${suffix}`;
}

/**
 * The default arena's id.
 *
 * It always exists, is never reaped, and is what a `join` carrying neither an
 * `arenaId` nor a `mapId` lands in — so the bare Deploy button behaves exactly
 * as it did before arenas were plural, without asking the server to invent a
 * room first.
 */
export const QUICK_MATCH_ARENA_ID = quickMatchIdFor(DEFAULT_MAP_ID);

/**
 * The arena id a client sends to be put in *its own* tutorial room.
 *
 * A sentinel rather than a real arena. No `Arena` is ever built under this id:
 * the registry translates it into a room private to the asking player
 * (`ArenaService.tutorialIdFor`), because the tutorial is fought one a side on
 * `drill` and a shared room would seat exactly two learners and refuse the
 * third. The client never has to know the resolved id — it asks for "the
 * tutorial" and `joined.arenaId` tells it which room that turned out to be,
 * exactly as it does for a quick match.
 *
 * **It is also the one join the server does not charge fuel for**, and the
 * reason that is safe is symmetry rather than trust: the tutorial room pays
 * nothing back either — no credits, no XP, no session, no combat record. A
 * hand-rolled client that sends this string gets a free scripted drill that
 * banks absolutely nothing, which is strictly worse than the quick match it
 * would otherwise be paying for. See `BattleGateway.onJoin`, and note that the
 * whole argument collapses the day the tutorial rewards anything.
 *
 * **This is why the tutorial is not in `CREATABLE_MISSION_KINDS`.** It became a
 * `MissionKind`, and every other mission is opened by `POST
 * /api/battle/missions` — which produces an ordinary paid room. Reaching the
 * tutorial through that route would charge a sortie for a room that banks
 * nothing *and* start banking for a room that charges nothing, depending on
 * which way the code drifted. It keeps its own way in, and this string is it.
 *
 * The **mark of completion** is the one thing the room does write, and it is not
 * a reward: `MissionRun` is a checkbox with nothing behind it — no credits, no
 * XP, no item, no rank — decided by the *server's* own `finishMission` rather
 * than by anything a client says. See `battle/mission-completion.ts`.
 */
export const TUTORIAL_ARENA_ID = 'tutorial';

/**
 * An arena as the list shows it.
 *
 * Deliberately not a snapshot of the match: the browsing player is not in it,
 * so what they need is whether there is a seat and who they would be joining,
 * not where anybody's tank is.
 */
export interface ArenaSummary {
  id: string;
  name: string;
  /** Humans currently in. Bots are counted separately because they yield a seat. */
  humans: number;
  capacity: number;
  bots: number;
  /** True for the quick-match arena, which is never reaped. */
  permanent: boolean;
  /**
   * Which map this arena is running, and its display name.
   *
   * On the summary because a room's map is the second thing a player wants to
   * know about it after whether there is a seat — and because it cannot change,
   * so it is a property of the arena rather than of the match in progress.
   */
  mapId: string;
  mapName: string;
  /**
   * Which rules this arena runs. Fixed for its life, like the map and for the
   * same reason.
   */
  mode: MatchMode;
  /**
   * True once a team battle has been decided.
   *
   * On the summary so the list can grey out a finished battle rather than
   * offering a seat in one that is over. A deathmatch is never finished.
   */
  finished?: boolean;
}

// ─── Socket events ───────────────────────────────────────────────────────────

/** Client → server. */
export interface ClientToServerEvents {
  /**
   * `mapId` is a *preference*, and only when `arenaId` is absent.
   *
   * Naming an arena names its map, because an arena's map is fixed; asking for
   * a room and a different map than it is running is a contradiction the server
   * resolves in favour of the room. With no arena named, the map chooses which
   * quick-match room to open. Either way the server echoes back what actually
   * happened in `joined`, and the client draws from that rather than from what
   * it asked for.
   */
  join: (payload: {
    nickname: string;
    clientId: string;
    arenaId?: string;
    mapId?: string;
    /** Which rules to play by. Only consulted when creating or picking a quick match. */
    mode?: MatchMode;
  }) => void;
  input: (payload: InputCommand) => void;
  ping: (payload: { sentAt: number }) => void;
  /**
   * Leaving on purpose, rather than by dropping the socket.
   *
   * No payload: what happens is decided entirely by what the arena already knows
   * about this seat, and a client that could say *how* it left could claim to have
   * stayed. The server answers once with `sortieClosed`.
   *
   * Dropping the socket still works and still banks — that is what a crash or a
   * closed tab does. The difference is only that nobody is left to answer.
   */
  leave: () => void;
}

/** Server → client. */
export interface ServerToClientEvents {
  joined: (payload: {
    playerId: string;
    nickname: string;
    spawn: TankState;
    tickRate: number;
    /**
     * Half-extent of *this arena's* playable rectangle, per world axis.
     *
     * A per-map figure since one battlefield wanted to be bigger than the rest,
     * and it is the arena's own answer rather than `ARENA_HALF_EXTENT` — a client
     * that assumed the default would predict its own movement against a wall in
     * a different place and be corrected on every tick spent near one.
     *
     * **A pair rather than one number**, for the reason on `ArenaHalfExtent`: a
     * scalar on the wire would be a scalar in every readout that consumes it,
     * and the radar would draw a square boundary round a rectangular arena
     * thirty times a second.
     *
     * The client can also derive it from `mapId` (it is `map.halfExtent`) and
     * does, beside the buildings, so the two cannot arrive out of step. This
     * field stays because a *readout* — the minimap, a probe — should not have
     * to resolve a map to ask how big the arena is.
     */
    arenaHalfExtent: ArenaHalfExtent;
    /**
     * Which arena this actually is.
     *
     * Echoed rather than assumed: a client that asked for one that had just been
     * reaped is put in quick match instead, and it needs to know which battle it
     * is in to say so.
     */
    arenaId: string;
    arenaName: string;
    /**
     * Which map that arena is running.
     *
     * The one field in this payload the client absolutely must not assume. Every
     * building the browser draws, and every wall its prediction resolves
     * against, comes from `mapById(mapId)` — so a client that guessed would
     * predict its own movement against different geometry than the server
     * stepped it with, which is a per-tick error near every wall and shows up
     * as nothing but a permanent reconciliation drift.
     *
     * The map itself never crosses the wire. This string is the whole payload
     * for two hundred buildings.
     */
    mapId: string;
    mapName: string;
    /**
     * Which rules this battle runs by.
     *
     * Echoed for the same reason `mapId` is: the client must not assume. It
     * decides whether the HUD counts down to a respawn or tells the player they
     * are about to inherit one of their side's tanks, and a client that guessed
     * wrong would show a countdown that never ends.
     */
    mode: MatchMode;
    /**
     * The loadout the server resolved for this player. The client needs it to
     * predict its own movement, reload and shells; it is a copy of what the
     * server already decided, never something the client gets to choose.
     */
    spec: TankSpec;
  }) => void;
  snapshot: (payload: ArenaSnapshot) => void;
  playerLeft: (payload: { playerId: string }) => void;
  pong: (payload: { sentAt: number; serverTime: number }) => void;
  rejected: (payload: { reason: string }) => void;
  /**
   * What the sortie paid, in reply to `leave`.
   *
   * `null` for a seat that banks nothing — a tutorial player, or a socket that was
   * not in an arena — so the client can tell "paid nothing" from "still waiting",
   * which a zero could not.
   */
  sortieClosed: (payload: SortieSettlement | null) => void;
}
